package link.erpango.quip.ui.transfer

import androidx.compose.foundation.Image
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.layout.*
import androidx.compose.material.CircularProgressIndicator
import androidx.compose.material.Icon
import androidx.compose.material.IconButton
import androidx.compose.material.MaterialTheme
import androidx.compose.material.Scaffold
import androidx.compose.material.Text
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.CheckCircle
import androidx.compose.material.icons.filled.Warning
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.navigation.NavController
import io.github.alexzhirkevich.qrose.rememberQrCodePainter
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.delay
import kotlinx.coroutines.isActive
import link.erpango.quip.data.remote.DResponse
import link.erpango.quip.domain.entities.QRData
import link.erpango.quip.features.transfer.RecipientState
import link.erpango.quip.features.transfer.TransferCode
import link.erpango.quip.features.transfer.TransferState
import link.erpango.quip.features.transfer.TransferViewModel
import link.erpango.quip.theme.QuipColors
import link.erpango.quip.ui.components.*
import link.erpango.quip.utils.DatetimeUtils

/** Intervalo de consulta del estado mientras la pantalla del emisor está visible. */
private const val STATUS_POLL_INTERVAL_MS = 5_000L

/** Refresco del texto "Caduca en …" (granularidad de minutos). */
private const val COUNTDOWN_REFRESH_MS = 30_000L

/** Espera tras dejar de escribir el ID antes de consultar el destinatario. */
private const val RECIPIENT_LOOKUP_DEBOUNCE_MS = 500L

@Composable
fun TransferQRScreen(
    navController: NavController,
    vehicleVin: String,
    viewModel: TransferViewModel,
    onNavigateBack: () -> Unit = {}
) {
    var targetUserId by remember { mutableStateOf("") }

    val transferState by viewModel.transferState.collectAsState()
    val recipientState by viewModel.recipientState.collectAsState()

    var isAccepted by remember { mutableStateOf(false) }

    // El ViewModel se cachea por VIN: al entrar se consulta al servidor si ya hay una
    // transferencia pendiente (200 -> QR/OTP directamente; 404 -> formulario).
    LaunchedEffect(vehicleVin) {
        viewModel.loadActiveTransfer()
    }

    // Consulta del destinatario (solo nombre de pila) con debounce mientras se escribe el ID.
    LaunchedEffect(targetUserId) {
        val id = targetUserId.trim().toIntOrNull()
        if (id == null) {
            viewModel.clearRecipient()
            return@LaunchedEffect
        }
        delay(RECIPIENT_LOOKUP_DEBOUNCE_MS)
        viewModel.lookupRecipient(id)
    }

    val activeToken = (transferState as? TransferState.QrGenerated)?.token

    // Polling del estado cada 5 s mientras la pantalla está visible, hasta un estado terminal
    // o hasta expires_at. La validez real la marca el servidor (24 h por defecto).
    LaunchedEffect(activeToken) {
        val token = activeToken ?: return@LaunchedEffect
        while (isActive) {
            val current = viewModel.transferState.value as? TransferState.QrGenerated ?: break
            if (TransferCode.isExpired(current.expiresAt, DatetimeUtils.currentTimeMillis())) {
                viewModel.markExpired()
                break
            }
            delay(STATUS_POLL_INTERVAL_MS)
            val response = try {
                viewModel.checkTransferStatus(token)
            } catch (e: CancellationException) {
                throw e
            } catch (_: Exception) {
                null // error transitorio: se reintenta en la siguiente vuelta
            }
            if (response is DResponse.Success) {
                if (response.data.isAccepted()) {
                    isAccepted = true
                    break
                }
                if (viewModel.onStatusPolled(response.data)) break
            }
        }
    }

    // Reloj para el texto de caducidad.
    var nowMs by remember { mutableLongStateOf(DatetimeUtils.currentTimeMillis()) }
    LaunchedEffect(activeToken) {
        if (activeToken == null) return@LaunchedEffect
        while (isActive) {
            nowMs = DatetimeUtils.currentTimeMillis()
            delay(COUNTDOWN_REFRESH_MS)
        }
    }

    Scaffold(
        backgroundColor = QuipColors.colorBackground
    ) { paddingValues ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            QuipTransparentHeader {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    IconButton(onClick = onNavigateBack) {
                        Icon(
                            Icons.Default.ArrowBack,
                            contentDescription = "Volver",
                            tint = QuipColors.colorText
                        )
                    }
                    Spacer(modifier = Modifier.width(8.dp))
                    Text(
                        text = if (isAccepted) "Transferencia Aceptada" else "Transferir Vehículo (QR)",
                        style = MaterialTheme.typography.h6,
                        fontWeight = FontWeight.Bold,
                        color = QuipColors.colorText
                    )
                }
            }

            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .imePadding()
                    .verticalScroll(rememberScrollState())
                    .padding(horizontal = 16.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                Spacer(modifier = Modifier.height(8.dp))

                val state = transferState
                when {
                    isAccepted -> AcceptedContent(navController)

                    state is TransferState.Expired -> TerminalContent(
                        title = "La transferencia ha caducado",
                        message = "El código ya no es válido. Genera una nueva transferencia si todavía quieres transferir el vehículo.",
                        color = QuipColors.colorWarning,
                        onNewTransfer = { viewModel.resetState() },
                        onBack = onNavigateBack
                    )

                    state is TransferState.Cancelled -> TerminalContent(
                        title = "Transferencia cancelada",
                        message = state.message,
                        color = QuipColors.colorTextSecondary,
                        onNewTransfer = { viewModel.resetState() },
                        onBack = onNavigateBack
                    )

                    state is TransferState.QrGenerated -> QrContent(
                        qrGen = state,
                        nowMs = nowMs,
                        onCancel = { viewModel.cancelTransfer(state.token) }
                    )

                    state is TransferState.Loading -> {
                        Spacer(modifier = Modifier.height(32.dp))
                        CircularProgressIndicator(color = QuipColors.colorPrimary)
                    }

                    else -> TransferForm(
                        targetUserId = targetUserId,
                        onTargetUserIdChange = { targetUserId = it.filter(Char::isDigit) },
                        recipientState = recipientState,
                        errorMessage = (state as? TransferState.Error)?.error,
                        onGenerate = { userId -> viewModel.initiateQrTransfer(userId) }
                    )
                }

                Spacer(modifier = Modifier.height(16.dp))
            }
        }
    }
}

@Composable
private fun AcceptedContent(navController: NavController) {
    Icon(
        Icons.Default.CheckCircle,
        contentDescription = "Éxito",
        tint = QuipColors.colorSuccess,
        modifier = Modifier.size(80.dp)
    )
    Text(
        text = "¡Transferencia aceptada!",
        style = MaterialTheme.typography.h5,
        fontWeight = FontWeight.Bold,
        color = QuipColors.colorSuccess,
        modifier = Modifier.fillMaxWidth(),
        textAlign = TextAlign.Center
    )
    Text(
        text = "El destinatario ha aceptado la transferencia del vehículo.",
        style = MaterialTheme.typography.body1,
        color = QuipColors.colorTextSecondary,
        modifier = Modifier.fillMaxWidth(),
        textAlign = TextAlign.Center
    )
    Spacer(modifier = Modifier.height(32.dp))

    LaunchedEffect(Unit) {
        delay(2000L)
        navController.popBackStack("home", inclusive = false)
    }

    QuipPrimaryButton(
        text = "Volver al inicio",
        onClick = { navController.popBackStack("home", inclusive = false) },
        icon = Icons.Default.ArrowBack,
        modifier = Modifier.fillMaxWidth()
    )
}

@Composable
private fun TerminalContent(
    title: String,
    message: String,
    color: Color,
    onNewTransfer: () -> Unit,
    onBack: () -> Unit
) {
    Icon(
        Icons.Default.Warning,
        contentDescription = null,
        tint = color,
        modifier = Modifier.size(80.dp)
    )
    Text(
        text = title,
        style = MaterialTheme.typography.h5,
        fontWeight = FontWeight.Bold,
        color = color,
        modifier = Modifier.fillMaxWidth(),
        textAlign = TextAlign.Center
    )
    Text(
        text = message,
        style = MaterialTheme.typography.body1,
        color = QuipColors.colorTextSecondary,
        modifier = Modifier.fillMaxWidth(),
        textAlign = TextAlign.Center
    )
    Spacer(modifier = Modifier.height(16.dp))
    QuipPrimaryButton(
        text = "Generar nueva transferencia",
        onClick = onNewTransfer,
        modifier = Modifier.fillMaxWidth()
    )
    QuipSecondaryButton(
        text = "Volver",
        onClick = onBack,
        icon = Icons.Default.ArrowBack,
        modifier = Modifier.fillMaxWidth()
    )
}

@Composable
private fun TransferForm(
    targetUserId: String,
    onTargetUserIdChange: (String) -> Unit,
    recipientState: RecipientState,
    errorMessage: String?,
    onGenerate: (Int) -> Unit
) {
    val typedId = targetUserId.trim().toIntOrNull()
    val resolved = (recipientState as? RecipientState.Found)?.takeIf { it.userId == typedId }

    QuipTextField(
        value = targetUserId,
        onValueChange = onTargetUserIdChange,
        label = "ID del usuario destino",
        modifier = Modifier.fillMaxWidth(),
        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
        isError = recipientState is RecipientState.Error && typedId != null,
        helperText = {
            when {
                typedId == null -> Text(
                    text = "Pide al destinatario su ID de usuario de Quip",
                    color = QuipColors.colorTextSecondary
                )
                resolved != null -> Text(
                    text = "Se transferirá a: ${resolved.firstName}",
                    color = QuipColors.colorSuccess,
                    fontWeight = FontWeight.Bold
                )
                recipientState is RecipientState.Error -> Text(
                    text = recipientState.message,
                    color = QuipColors.colorError
                )
                else -> Text(text = "Comprobando usuario…", color = QuipColors.colorTextSecondary)
            }
        }
    )

    Text(
        text = "El código será válido durante 24 horas.",
        style = MaterialTheme.typography.caption,
        color = QuipColors.colorTextSecondary,
        modifier = Modifier.fillMaxWidth()
    )

    QuipPrimaryButton(
        text = "Iniciar transferencia QR",
        onClick = { resolved?.let { onGenerate(it.userId) } },
        enabled = resolved != null,
        modifier = Modifier.fillMaxWidth()
    )

    if (errorMessage != null) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Icon(
                Icons.Default.Warning,
                contentDescription = null,
                tint = QuipColors.colorError,
                modifier = Modifier.size(20.dp)
            )
            Spacer(modifier = Modifier.width(8.dp))
            Text(
                text = "Error: $errorMessage",
                color = QuipColors.colorError,
                style = MaterialTheme.typography.body2,
                modifier = Modifier.weight(1f)
            )
        }
    }
}

@Composable
private fun QrContent(
    qrGen: TransferState.QrGenerated,
    nowMs: Long,
    onCancel: () -> Unit
) {
    if (qrGen.alreadyActive) {
        Text(
            text = "Este vehículo ya tenía una transferencia pendiente. Puedes seguir usando este código o cancelarla.",
            style = MaterialTheme.typography.body2,
            color = QuipColors.colorTextSecondary,
            modifier = Modifier.fillMaxWidth(),
            textAlign = TextAlign.Center
        )
    }

    QuipCard {
        Column(
            modifier = Modifier.fillMaxWidth(),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            qrGen.targetUserFirstName?.let { name ->
                Text(
                    text = "Para: $name",
                    style = MaterialTheme.typography.h6,
                    fontWeight = FontWeight.Bold,
                    color = QuipColors.colorText
                )
            }

            Text(
                text = "Pide al destinatario que escanee este código QR",
                style = MaterialTheme.typography.body1,
                color = QuipColors.colorText,
                textAlign = TextAlign.Center
            )

            // El QR codifica el qr_data devuelto por el servidor (incluye el token largo).
            val qrJsonData = remember(qrGen.qrData) { QRData.toJson(qrGen.qrData) }
            Image(
                painter = rememberQrCodePainter(qrJsonData),
                contentDescription = "Código QR de transferencia",
                modifier = Modifier.size(240.dp)
            )

            Column(horizontalAlignment = Alignment.CenterHorizontally) {
                Text(
                    text = TransferCode.formatExpiryRemaining(qrGen.expiresAt, nowMs),
                    style = MaterialTheme.typography.body2,
                    fontWeight = FontWeight.Bold,
                    color = QuipColors.colorText
                )
                Text(
                    text = "Válido hasta el ${TransferCode.formatExpiryDateTime(qrGen.expiresAt)}",
                    style = MaterialTheme.typography.caption,
                    color = QuipColors.colorTextSecondary
                )
            }

            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp, Alignment.CenterHorizontally),
                modifier = Modifier.fillMaxWidth()
            ) {
                CircularProgressIndicator(
                    color = QuipColors.colorPrimary,
                    strokeWidth = 2.dp,
                    modifier = Modifier.size(16.dp)
                )
                Text(
                    text = "Esperando aceptación del receptor...",
                    style = MaterialTheme.typography.caption,
                    color = QuipColors.colorTextSecondary
                )
            }

            CollapsibleOtpFallback(code = qrGen.otpCode ?: qrGen.token)
        }
    }

    QuipSecondaryButton(
        text = "Cancelar transferencia",
        onClick = onCancel,
        modifier = Modifier.fillMaxWidth()
    )
}

/**
 * Código para introducir a mano cuando no se puede escanear: el OTP corto (`ABCD-EFGH`).
 * Con backends anteriores al OTP se recibe el token largo como respaldo.
 */
@Composable
fun CollapsibleOtpFallback(code: String) {
    var expanded by remember { mutableStateOf(false) }
    var copied by remember { mutableStateOf(false) }
    @Suppress("DEPRECATION")
    val clipboard = LocalClipboardManager.current

    LaunchedEffect(copied) {
        if (copied) {
            delay(2000L)
            copied = false
        }
    }

    Column(modifier = Modifier.fillMaxWidth()) {
        QuipTextButton(
            text = if (expanded) "Ocultar código manual" else "¿No puedes escanear? Usa el código manual",
            onClick = { expanded = !expanded },
            modifier = Modifier.align(Alignment.CenterHorizontally)
        )

        if (expanded) {
            Spacer(modifier = Modifier.height(8.dp))
            QuipCard {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    Text(
                        text = "Código de transferencia:",
                        style = MaterialTheme.typography.body2,
                        color = QuipColors.colorText
                    )

                    val isShort = code.length <= 9
                    SelectionContainer {
                        Text(
                            text = code,
                            style = if (isShort) MaterialTheme.typography.h4 else MaterialTheme.typography.body2,
                            fontFamily = FontFamily.Monospace,
                            fontWeight = FontWeight.Bold,
                            letterSpacing = if (isShort) 4.sp else 0.sp,
                            color = QuipColors.colorText,
                            textAlign = TextAlign.Center
                        )
                    }

                    QuipTextButton(
                        text = if (copied) "Código copiado" else "Copiar código",
                        onClick = {
                            clipboard.setText(AnnotatedString(code))
                            copied = true
                        },
                        icon = if (copied) Icons.Default.CheckCircle else null
                    )

                    Text(
                        text = "Envía este código al destinatario para que lo introduzca en " +
                            "\"Aceptar transferencia\". No distingue mayúsculas ni guiones.",
                        style = MaterialTheme.typography.caption,
                        color = QuipColors.colorTextSecondary,
                        textAlign = TextAlign.Center
                    )
                }
            }
        }
    }
}
